<template>
  <el-dialog
    :title="isEdit ? '编辑公司' : '新增公司'"
    :visible.sync="innerVisible"
    width="720px"
    custom-class="company-form-dialog"
    :close-on-click-modal="false"
    append-to-body
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="110px"
    >
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="公司类型" prop="type">
            <el-radio-group v-model="form.type">
              <el-radio :label="1">主公司</el-radio>
              <el-radio :label="2">合作公司</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="上级公司" prop="parent_id">
            <el-select
              v-model="form.parent_id"
              placeholder="请选择上级公司（可不选）"
              clearable
              :disabled="form.type === 1"
              style="width: 100%"
            >
              <el-option
                v-for="item in parentOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="公司名称" prop="name" class="after-row-item">
        <el-input v-model="form.name" placeholder="请输入公司全称" maxlength="128" />
      </el-form-item>

      <el-form-item label="公司简称" prop="short_name">
        <el-input v-model="form.short_name" placeholder="用于列表与下拉展示" maxlength="64" />
      </el-form-item>

      <el-form-item label="公司LOGO">
        <image-uploader v-model="form.logo" :hint="''" />
      </el-form-item>

      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item label="联系人" prop="contact_name">
            <el-input v-model="form.contact_name" placeholder="请输入联系人" maxlength="64" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系电话" prop="contact_phone">
            <el-input v-model="form.contact_phone" placeholder="请输入联系电话" maxlength="32" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="所在地区" class="region-form-item">
        <el-row :gutter="8">
          <el-col :span="7">
            <el-select v-model="form.province" placeholder="省" clearable filterable style="width: 100%" @change="onProvinceChange">
              <el-option v-for="p in provinces" :key="p.code" :label="p.name" :value="p.code" />
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-select v-model="form.city" placeholder="市" clearable filterable style="width: 100%" @change="onCityChange">
              <el-option v-for="c in cities" :key="c.code" :label="c.name" :value="c.code" />
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-select v-model="form.district" placeholder="区/县" clearable filterable style="width: 100%">
              <el-option v-for="a in areas" :key="a.code" :label="a.name" :value="a.code" />
            </el-select>
          </el-col>
        </el-row>
      </el-form-item>

      <el-form-item label="详细地址">
        <el-input v-model="form.address" placeholder="街道门牌号" maxlength="255" />
      </el-form-item>

      <el-form-item label="状态">
        <el-switch
          v-model="form.status"
          :active-value="1"
          :inactive-value="0"
          active-text="正常"
          inactive-text="停用"
        />
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button @click="innerVisible = false">取 消</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue'
import { createCompany, updateCompany } from '@/api/system'
import { getProvince, getCity, getArea } from '@/api/public'

const emptyForm = () => ({
  id: 0,
  name: '',
  short_name: '',
  logo: '',
  type: 2,
  // 未选择时留空显示 placeholder（存库时归一化为 0），别把 0 直接渲染出来
  parent_id: undefined,
  contact_name: '',
  contact_phone: '',
  province: '',
  city: '',
  district: '',
  address: '',
  status: 1
})

export default {
  name: 'CompanyForm',
  components: { ImageUploader },
  props: {
    visible: { type: Boolean, default: false },
    row: { type: Object, default: null },
    companyOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: emptyForm(),
      submitting: false,
      provinces: [], // 省市区三级联动（走公共接口，存编码提交）
      cities: [],
      areas: [],
      rules: {
        name: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
        contact_name: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
        contact_phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$|^400-?\d{3}-?\d{4}$/, message: '手机号或座机格式不正确', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    isEdit() {
      return !!(this.row && this.row.id)
    },
    // 上级公司不能选自己，也不能选自己的子孙（避免成环）
    parentOptions() {
      if (!this.isEdit) return this.companyOptions
      const descendants = this.$parent.collectDescendants
        ? this.$parent.collectDescendants(this.row.id)
        : []
      const exclude = [this.row.id].concat(descendants)
      return this.companyOptions.filter(item => exclude.indexOf(item.id) === -1)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.form = this.row ? Object.assign(emptyForm(), this.row) : emptyForm()
        // 回显时 parent_id=0（顶级）转空，避免下拉框渲染出 "0"
        if (!this.form.parent_id) this.form.parent_id = undefined
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.clearValidate()
        })
        this.initRegion()
      }
    },
    'form.type'(val) {
      if (val === 1) this.form.parent_id = undefined
    }
  },
  methods: {
    // 省市区联动：打开弹框时拉省列表；表单存编码，编辑回显按已存编码逐级拉市/区
    initRegion() {
      getProvince().then(res => {
        this.provinces = res.data || []
        this.cities = []
        this.areas = []
        const p = this.provinces.find(i => i.code === this.form.province)
        if (p) {
          this.loadCities(p.code, () => {
            const c = this.cities.find(i => i.code === this.form.city)
            if (c) this.loadAreas(c.code)
          })
        }
      })
    },
    loadCities(code, done) {
      getCity(code).then(res => {
        this.cities = res.data || []
        if (done) done()
      })
    },
    loadAreas(code) {
      getArea(code).then(res => {
        this.areas = res.data || []
      })
    },
    // 值绑的是编码（公司表存编码），change 回调直接拿编码拉下级
    onProvinceChange(code) {
      this.form.city = ''
      this.form.district = ''
      this.cities = []
      this.areas = []
      if (code) this.loadCities(code)
    },
    onCityChange(code) {
      this.form.district = ''
      this.areas = []
      if (code) this.loadAreas(code)
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const { id, ...rest } = this.form
        // parent_id 未选择（undefined/''）时归一化为 0（顶级），后端约定是数字
        rest.parent_id = rest.parent_id || 0
        // 编辑不传 id 字段；主公司 parent_id 已由 watcher 强制为 0，后端再兜底
        const request = this.isEdit ? updateCompany(id, rest) : createCompany(rest)
        request.then(() => {
          this.$message.success(this.isEdit ? '已保存修改' : '新增成功')
          this.innerVisible = false
          this.$emit('saved')
        }).finally(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>

<style>
/* 弹框高度控制（append-to-body 的弹框用 custom-class 命名空间做非 scoped 覆盖）：
   收紧内容区 padding + 表单行距，高度由全局 .el-dialog max-height 兜底 */
.company-form-dialog .el-dialog__body {
  padding: 14px 30px 4px;
}
.company-form-dialog .el-form-item {
  margin-bottom: 14px;
}
.company-form-dialog .el-form-item:last-child {
  margin-bottom: 2px;
}
/* 省市区一行三个：float + 31% 宽 + 2% 间距（总 97% 留余量，别用 33% 会溢出掉行），
   ::after 清 float 撑高容器，否则所在地区行高塌陷吃掉与上一行的间距 */
.company-form-dialog .region-select {
  float: left;
  width: 31%;
  margin-right: 2%;
}
.company-form-dialog .region-select--last {
  margin-right: 0;
}
.company-form-dialog .region-clear::after {
  content: "";
  display: block;
  clear: both;
}
/* el-row/el-col 是 float 容器，内部 form-item 的 margin-bottom 不计入行高（实测塌到 2px），
   给 float 行的下一条 form-item 补回 margin-top，量过全表行距后定的值。
   命中两处：公司类型/上级公司行 → 公司名称；联系人/电话行 → 所在地区 */
.company-form-dialog .region-form-item,
.company-form-dialog .after-row-item {
  margin-top: 14px;
}
</style>
